class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this._lastRefreshAt = 0;
    this._csQtyBusy = false;
    this._refreshPromise = null;
    this._scheduledRefreshTimer = null;
    this._scheduledRefreshOpen = false;
    this._pendingMutations = new Set();
    this._mutationQueue = Promise.resolve();
    this._groupedMutationDraft = new Map();
    this._groupedFlushTimer = null;
    this._groupedFlushPromise = null;
    this._groupedActiveFlushPromise = null;
    this._deferredRefreshTimer = null;
    this._groupedMutationVersion = 0;
    this._pendingSyncStorageKey = 'cs_cart_pending_grouped_sync_v1';
    this._cachedEmptyStateMarkup = '';
    this._primeEmptyMarkupPromise = null;
    this._awaitingCheckoutSubmit = false;
    this._checkoutBusyState = false;
    this._priceLoadingPendingKeys = new Set();
    this._priceLoadingReleaseTimer = null;
    this._freeGiftSyncTimer = null;
    this._freeGiftSyncPromise = null;
    this._freeGiftSyncQueued = false;
    this._freeGiftSyncError = null;
    this._giftCheckpointImageCache = new Map();
    this._giftCheckpointImagePending = new Set();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    // Warm the lazily loaded upsell carousel script on the first user interaction (never fired by lab runs).
    ['pointerdown', 'touchstart', 'keydown', 'mousemove', 'wheel', 'scroll'].forEach((t) => document.addEventListener(t, () => this.ensureUpsellScript(), { once: true, passive: true, capture: true }));

    // Grouped qty/remove controls (same-variant lines rendered as one entry)
    this.addEventListener('click', (event) => {
      const qtyButton = event.target.closest('[data-cs-qty-action]');
      const removeButton = event.target.closest('[data-cs-remove-group]');
      const bundleRemoveButton = event.target.closest('[data-cs-remove-bundle]');
      const lockedItem = event.target.closest('.cs-cart-item[data-cs-locked="true"]');
      if (lockedItem && (qtyButton || removeButton || bundleRemoveButton)) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (qtyButton) this.onGroupedQtyClick(event, qtyButton);
      if (removeButton) this.onGroupedRemoveClick(event, removeButton);
      if (bundleRemoveButton) this.onBundleRemoveClick(event, bundleRemoveButton);

      const checkoutBtn = event.target.closest('#CartDrawer-Checkout, button[name="checkout"][form="CartDrawer-Form"]');
      if (checkoutBtn) {
        event.preventDefault();
        event.stopPropagation();
        this.startCheckoutFlow(checkoutBtn);
      }
    });

    // Returning from checkout (or any back/forward navigation) can leave drawer DOM stale.
    // Refresh sections silently on pageshow, but only for bfcache / back-forward restores:
    // a normal navigation already ships freshly server-rendered drawer markup, and open()
    // re-syncs anything older than 10 s anyway.
    window.addEventListener('pageshow', (event) => {
      // iOS bfcache can restore a stale disabled/busy checkout button state.
      this._awaitingCheckoutSubmit = false;
      this._checkoutBusyState = false;
      this.applyCheckoutBusyState();
      let backForward = false;
      try {
        const nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
        backForward = Boolean(nav && nav.type === 'back_forward');
      } catch (_) {}
      if (event.persisted || backForward) this.requestRefresh();
    });

    // Custom scripts (apps/custom ATC) may dispatch this instead of Dawn pubsub.
    document.addEventListener('cart:updated', (event) => {
      if (event?.detail?.skipDrawerRefresh) return;
      const suppressUntil = parseInt(this.dataset.psmSuppressRefreshUntil || '0', 10) || 0;
      if (Date.now() < suppressUntil) return;
      this.requestRefresh();
    });

    document.addEventListener('cs:subscription-incentive-changed', () => this.scheduleFreeGiftSync());

    this._onCheckoutSubmitBound = this.onCheckoutSubmit.bind(this);
    this.addEventListener('submit', this._onCheckoutSubmitBound, true);
    this._onDocumentNavIntentBound = this.onDocumentNavigationIntent.bind(this);
    document.addEventListener('click', this._onDocumentNavIntentBound, true);
    document.addEventListener('submit', this._onDocumentNavIntentBound, true);
    this._onPageHideFlushBound = this.onPageHideFlush.bind(this);
    window.addEventListener('pagehide', this._onPageHideFlushBound);
    document.addEventListener('visibilitychange', this._onPageHideFlushBound);
    this._onGroovyCreditStateBound = () => this.syncGroovyCreditProgress();
    document.addEventListener('groovy:credit-state', this._onGroovyCreditStateBound);
    this.resumePendingSyncFromStorage();
    this.scheduleFreeGiftSync();
    this.syncLocalizedChrome();
    this.syncGroovyCreditProgress();
  }

  isGroovyCreditCampaignActive() {
    try {
      return localStorage.getItem('groovy_credit_campaign_v1') === 'active';
    } catch (_) {
      return document.documentElement.hasAttribute('data-groovy-credit-active');
    }
  }

  formatGroovyCreditMoney(cents) {
    const amount = (Math.max(0, parseInt(cents, 10) || 0)) / 100;
    const currency = window.Shopify?.currency?.active || 'USD';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
    } catch (_) {
      return `$${amount.toFixed(2)}`;
    }
  }

  syncGroovyCreditProgress() {
    const widget = this.querySelector('[data-groovy-credit-progress]');
    if (!widget) return;

    const active = this.isGroovyCreditCampaignActive();
    document.documentElement.toggleAttribute('data-groovy-credit-active', active);
    widget.hidden = !active;
    if (!active) return;

    const total = Math.max(0, parseInt(widget.dataset.cartTotal || '0', 10) || 0);
    const threshold = Math.max(1, parseInt(widget.dataset.threshold || '14997', 10) || 14997);
    const credit = Math.max(1, parseInt(widget.dataset.credit || '5000', 10) || 5000);
    const unlockedCount = Math.floor(total / threshold);
    const tierSpend = total - unlockedCount * threshold;
    const remaining = tierSpend === 0 && total > 0 ? threshold : Math.max(0, threshold - tierSpend);
    const progress = tierSpend === 0 && total > 0 ? 0 : Math.min(100, (tierSpend / threshold) * 100);
    const unlockedCredit = unlockedCount * credit;
    const nextCredit = (unlockedCount + 1) * credit;

    widget.style.setProperty('--groovy-credit-fill', `${progress}%`);
    const title = widget.querySelector('[data-groovy-credit-title]');
    const ratio = widget.querySelector('[data-groovy-credit-ratio]');
    const status = widget.querySelector('[data-groovy-credit-status]');
    const track = widget.querySelector('[data-groovy-credit-track]');

    if (title) {
      title.textContent =
        unlockedCount > 0
          ? `${this.formatGroovyCreditMoney(unlockedCredit)} unlocked`
          : `${this.formatGroovyCreditMoney(credit)} at checkout`;
    }
    if (ratio) {
      ratio.textContent = `${this.formatGroovyCreditMoney(tierSpend)} / ${this.formatGroovyCreditMoney(threshold)}`;
    }
    if (status) {
      status.textContent =
        unlockedCount > 0
          ? `Add ${this.formatGroovyCreditMoney(remaining)} more to unlock ${this.formatGroovyCreditMoney(nextCredit)} total credit.`
          : `Add ${this.formatGroovyCreditMoney(remaining)} more to unlock your first ${this.formatGroovyCreditMoney(credit)} credit.`;
    }
    if (track) {
      track.setAttribute('aria-valuemax', String(threshold));
      track.setAttribute('aria-valuenow', String(tierSpend));
    }
  }

  setPriceLoadingState(isLoading) {
    this.classList.toggle('cs-cart-drawer--price-loading', Boolean(isLoading));
  }

  clearPriceLoadingRows() {
    this.querySelectorAll('[data-cs-price-loading="true"]').forEach((el) => {
      el.removeAttribute('data-cs-price-loading');
      el.removeAttribute('data-cs-price-loading-key');
    });
  }

  getPriceLoadingKeyFromItem(itemEl) {
    if (!itemEl) return `global-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const signature = this.getGroupedSignatureFromItem(itemEl);
    return signature || `item-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }

  beginPriceLoading(activeItem = null) {
    const key = this.getPriceLoadingKeyFromItem(activeItem);
    this._priceLoadingPendingKeys.add(key);
    if (this._priceLoadingReleaseTimer) {
      clearTimeout(this._priceLoadingReleaseTimer);
      this._priceLoadingReleaseTimer = null;
    }
    if (activeItem) {
      activeItem.setAttribute('data-cs-price-loading', 'true');
      activeItem.setAttribute('data-cs-price-loading-key', key);
    }
    this.setPriceLoadingState(true);
    return key;
  }

  endPriceLoading({ deferMs = 0, key = '', clearAll = false } = {}) {
    if (this._priceLoadingReleaseTimer) clearTimeout(this._priceLoadingReleaseTimer);

    if (clearAll) {
      this._priceLoadingPendingKeys.clear();
      const finalizeAll = () => {
        this.setPriceLoadingState(false);
        this.clearPriceLoadingRows();
      };
      if (deferMs > 0) {
        this._priceLoadingReleaseTimer = setTimeout(() => {
          this._priceLoadingReleaseTimer = null;
          finalizeAll();
        }, deferMs);
        return;
      }
      finalizeAll();
      return;
    }

    if (key) this._priceLoadingPendingKeys.delete(key);
    const finalize = () => {
      if (key) {
        this.querySelectorAll('[data-cs-price-loading-key]').forEach((el) => {
          if ((el.getAttribute('data-cs-price-loading-key') || '') !== String(key)) return;
          el.removeAttribute('data-cs-price-loading');
          el.removeAttribute('data-cs-price-loading-key');
        });
      }
      if (this._priceLoadingPendingKeys.size === 0) this.setPriceLoadingState(false);
    };

    if (deferMs > 0) {
      this._priceLoadingReleaseTimer = setTimeout(() => {
        this._priceLoadingReleaseTimer = null;
        finalize();
      }, deferMs);
      return;
    }
    finalize();
  }

  syncEmptyStateClass() {
    // Our CSS + focus trap logic depends on cart-drawer.is-empty, but we only
    // replace inner HTML on refresh. Keep the root class in sync with DOM.
    const itemsEl = this.querySelector('cart-drawer-items');
    const isEmpty =
      (itemsEl && itemsEl.classList.contains('is-empty')) || Boolean(this.querySelector('.cs-cart-drawer__empty'));
    this.classList.toggle('is-empty', isEmpty);
    this.setDrawerEmptyVisualState(isEmpty);
  }

  setDrawerEmptyVisualState(isEmpty) {
    const footer = this.querySelector('.drawer__footer');
    if (footer) footer.toggleAttribute('hidden', isEmpty);

    const nonEmptyUpsell = this.querySelector('cs-cart-upsell:not(.cs-cart-upsell--empty)');
    if (nonEmptyUpsell) nonEmptyUpsell.toggleAttribute('hidden', isEmpty);

    const checkoutBtn = this.querySelector('#CartDrawer-Checkout');
    if (checkoutBtn) {
      if (isEmpty) checkoutBtn.setAttribute('disabled', 'disabled');
      else checkoutBtn.removeAttribute('disabled');
    }

    // Keep checkout loading state visible even when drawer footer re-renders.
    this.applyCheckoutBusyState();
  }

  getCheckoutSubmitButtons() {
    return Array.from(this.querySelectorAll('#CartDrawer-Checkout, button[name="checkout"][form="CartDrawer-Form"]'));
  }

  applyCheckoutBusyState() {
    const isBusy = Boolean(this._awaitingCheckoutSubmit || this._checkoutBusyState);
    const isEmpty = this.classList.contains('is-empty');
    this.getCheckoutSubmitButtons().forEach((btn) => {
      if (!(btn instanceof HTMLElement)) return;
      if (isBusy) {
        btn.setAttribute('aria-busy', 'true');
        btn.setAttribute('disabled', 'disabled');
        return;
      }
      btn.removeAttribute('aria-busy');
      if (!isEmpty) btn.removeAttribute('disabled');
    });
  }

  getScrollContainer() {
    // Dawn drawers typically scroll the inner panel; fall back to contents.
    return (
      this.querySelector('#CartDrawer-CartItems') ||
      this.querySelector('.drawer__contents') ||
      this.querySelector('.drawer__inner') ||
      this.querySelector('.drawer__contents')
    );
  }

  captureScrollPositions() {
    // Different themes/app CSS may make different elements scroll.
    const candidates = [
      '#CartDrawer',
      '.drawer__inner',
      '.drawer__contents',
      '#CartDrawer-CartItems',
      'cart-drawer-items',
    ];

    const positions = {};
    candidates.forEach((sel) => {
      const el = this.querySelector(sel);
      if (!el) return;
      positions[sel] = el.scrollTop || 0;
    });

    return positions;
  }

  restoreScrollPositions(positions) {
    if (!positions) return;
    const apply = () => {
      Object.keys(positions).forEach((sel) => {
        const el = this.querySelector(sel);
        if (!el) return;
        el.scrollTop = positions[sel];
      });
    };

    // Apply a few times to beat focus-trap / layout reflow
    requestAnimationFrame(apply);
    setTimeout(apply, 0);
    setTimeout(apply, 60);
    setTimeout(apply, 140);
  }

  updateFooterHeightVar() {
    const footer = this.querySelector('.drawer__footer');
    const height = footer ? footer.offsetHeight : 0;
    this.style.setProperty('--cs-cart-drawer-footer-height', `${height}px`);
  }

  readCartBubbleCount() {
    const bubble = document.querySelector('#cart-icon-bubble');
    if (!bubble) return 0;
    const countEl =
      bubble.querySelector('.cart-count-bubble span[aria-hidden="true"]') ||
      bubble.querySelector('.cart-count-bubble');
    const text = (countEl?.textContent || bubble.textContent || '').trim();
    const n = parseInt((text.match(/\d+/) || [0])[0], 10);
    return Number.isFinite(n) ? n : 0;
  }

  setCartBubbleCount(nextCount) {
    const bubble = document.querySelector('#cart-icon-bubble');
    if (!bubble) return;
    const count = Math.max(0, parseInt(nextCount, 10) || 0);
    let container = bubble.querySelector('.cart-count-bubble');
    let visibleCount = bubble.querySelector('.cart-count-bubble span[aria-hidden="true"]');
    let srCount = bubble.querySelector('.cart-count-bubble span.visually-hidden');

    // Empty-cart header markup can lack the bubble node entirely.
    // Create it on-demand so optimistic count updates are always visible.
    if (!container && count > 0) {
      container = document.createElement('div');
      container.className = 'cart-count-bubble';

      visibleCount = document.createElement('span');
      visibleCount.setAttribute('aria-hidden', 'true');
      container.appendChild(visibleCount);

      srCount = document.createElement('span');
      srCount.className = 'visually-hidden';
      container.appendChild(srCount);

      bubble.appendChild(container);
    }

    if (visibleCount) visibleCount.textContent = String(count);
    if (srCount) {
      const countKey = count === 1 ? 'cartCountOne' : 'cartCountOther';
      const countFallback = count === 1 ? '{{ count }} item' : '{{ count }} items';
      srCount.textContent = window.pdpString
        ? window.pdpString(countKey, countFallback, { count: String(count) })
        : `${count} items`;
    }
    if (container) container.style.display = count > 0 ? '' : 'none';
  }

  adjustCartBubbleCount(delta) {
    const current = this.readCartBubbleCount();
    this.setCartBubbleCount(current + (parseInt(delta, 10) || 0));
  }

  getFreeGiftRules() {
    const raw = String(this.dataset.csFreeGiftRules || '').trim();
    if (!raw) return window.csSubscriptionIncentive?.giftRule() ? [window.csSubscriptionIncentive.giftRule()] : [];
    let parsed = [];
    try {
      parsed = JSON.parse(raw);
    } catch (_) {
      return [];
    }
    if (!Array.isArray(parsed)) return [];
    const seen = new Set();
    const rules = parsed
      .map((rule, idx) => {
        const id = String(rule?.id || idx + 1);
        const variantId = parseInt(rule?.variantId, 10);
        const thresholdCents = Math.max(0, parseInt(rule?.thresholdCents, 10) || 0);
        if (!id || !variantId || !thresholdCents || seen.has(id)) return null;
        seen.add(id);
        return { id, variantId, thresholdCents };
      })
      .filter(Boolean)
      .sort((a, b) => a.thresholdCents - b.thresholdCents);
    const subscriptionGift = window.csSubscriptionIncentive?.giftRule();
    if (subscriptionGift) rules.push(subscriptionGift);
    return rules;
  }

  isFreeGiftLineItem(item) {
    const props = item?.properties;
    if (!props || typeof props !== 'object') return false;
    return String(props._cs_free_gift || '').toLowerCase() === 'true';
  }

  scheduleFreeGiftSync({ immediate = false } = {}) {
    const run = () => {
      if (this._freeGiftSyncPromise) {
        this._freeGiftSyncQueued = true;
        return this._freeGiftSyncPromise;
      }

      const rules = this.getFreeGiftRules();
      let emptyCart = false;
      try {
        emptyCart = this.classList.contains('is-empty');
      } catch (_) {}
      // No rules to apply and nothing in the cart to clean up: skip the /cart.js round trip.
      if (!rules.length && emptyCart) return Promise.resolve();
      this._freeGiftSyncPromise = (rules.length ? this.syncFreeGiftWithCart(rules) : this.removeDisabledFreeGiftLines())
        .then(() => { this._freeGiftSyncError = null; })
        .catch((error) => { this._freeGiftSyncError = error; })
        .finally(() => {
          this._freeGiftSyncPromise = null;
          if (this._freeGiftSyncQueued) {
            this._freeGiftSyncQueued = false;
            this.scheduleFreeGiftSync({ immediate: true });
          }
        });
      return this._freeGiftSyncPromise;
    };

    if (immediate) {
      if (this._freeGiftSyncTimer) {
        clearTimeout(this._freeGiftSyncTimer);
        this._freeGiftSyncTimer = null;
      }
      return run();
    }

    if (this._freeGiftSyncTimer) return this._freeGiftSyncPromise || Promise.resolve();
    this._freeGiftSyncTimer = setTimeout(() => {
      this._freeGiftSyncTimer = null;
      run();
    }, 70);
    return this._freeGiftSyncPromise || Promise.resolve();
  }

  async removeDisabledFreeGiftLines() {
    if (this.hasPendingCartMutations()) {
      await this.flushAllPendingNow().catch(() => {});
    }
    await window.csSubscriptionIncentive?.waitForSelectionWrites();
    let cart = await fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' }).then((r) => r.json());
    cart = await this.normalizeSubscriptionIncentiveCart(cart);
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const giftLines = items.filter((it) => this.isFreeGiftLineItem(it));
    if (!giftLines.length) return;
    const updates = {};
    giftLines.forEach((line) => {
      updates[line.key] = 0;
    });
    await this.updateManyLineItems(updates);
    this.requestRefresh({ open: this.classList.contains('active'), immediate: true });
  }

  hydrateGiftCheckpointImages() {
    const checkpoints = this.querySelectorAll('.cs-cart-drawer__gift-checkpoint[data-cs-variant-id]');
    if (!checkpoints.length) return;

    const applyImage = (checkpoint, imageUrl) => {
      if (!checkpoint || !imageUrl) return;
      const thumb = checkpoint.querySelector('.cs-cart-drawer__gift-thumb');
      if (!thumb || thumb.querySelector('img')) return;
      const fallback = thumb.querySelector('span');
      if (fallback) fallback.remove();
      const img = document.createElement('img');
      img.src = imageUrl;
      img.alt = checkpoint.dataset.csGiftAlt || (window.pdpString ? window.pdpString('gift', 'Gift') : 'Gift');
      img.loading = 'lazy';
      img.width = 42;
      img.height = 42;
      thumb.appendChild(img);
    };

    checkpoints.forEach((checkpoint) => {
      const variantId = parseInt(checkpoint.dataset.csVariantId || '', 10);
      if (!variantId) return;
      const cacheKey = String(variantId);
      if (this._giftCheckpointImageCache.has(cacheKey)) {
        const cached = this._giftCheckpointImageCache.get(cacheKey);
        if (cached) applyImage(checkpoint, cached);
        return;
      }
      if (this._giftCheckpointImagePending.has(cacheKey)) return;
      this._giftCheckpointImagePending.add(cacheKey);

      fetch(`/variants/${variantId}.js`, { credentials: 'same-origin', cache: 'force-cache' })
        .then((res) => (res.ok ? res.json() : null))
        .then((variant) => {
          const url = variant?.featured_image?.src || variant?.featured_image || '';
          this._giftCheckpointImageCache.set(cacheKey, url || '');
          if (url) applyImage(checkpoint, url);
        })
        .catch(() => {
          this._giftCheckpointImageCache.set(cacheKey, '');
        })
        .finally(() => {
          this._giftCheckpointImagePending.delete(cacheKey);
        });
    });
  }

  async normalizeSubscriptionIncentiveCart(cart) {
    return window.csSubscriptionIncentive ? window.csSubscriptionIncentive.normalizeCart(cart, (body) =>
      this.queueMutation(() => this.requestJson('/cart/change.js', {
        method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
      }))
    ) : cart;
  }

  async syncFreeGiftWithCart(rules = this.getFreeGiftRules()) {
    if (!Array.isArray(rules) || !rules.length) return;
    if (this.hasPendingCartMutations()) {
      await this.flushAllPendingNow().catch(() => {});
    }

    await window.csSubscriptionIncentive?.waitForSelectionWrites();
    let cart = await fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' }).then((r) => r.json());
    cart = await this.normalizeSubscriptionIncentiveCart(cart);
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const giftLines = items.filter((it) => this.isFreeGiftLineItem(it));
    const cartTotal = parseInt(cart?.total_price, 10) || 0;
    const giftTotal = giftLines.reduce((sum, line) => sum + (parseInt(line.final_line_price, 10) || 0), 0);
    const qualifyingTotal = Math.max(0, cartTotal - giftTotal);
    const updates = {};
    const additions = [];
    const matchedGiftKeys = new Set();
    let didMutate = false;

    const subscriptionRule = rules.find((rule) => rule.kind === 'subscription');
    if (subscriptionRule && window.csSubscriptionIncentive.qualifies(subscriptionRule, cart)) {
      await window.csSubscriptionIncentive.ensureGiftAvailable();
    }
    rules.forEach((rule, idx) => {
      const linesForRule = giftLines.filter((line) => {
        // Subscription eligibility never makes the gift itself a subscription.
        // Reject recurring gifts even when matching legacy lines without a rule ID.
        if (rule.kind === 'subscription' &&
          (Number(line.variant_id) !== rule.variantId || line.selling_plan_allocation)) return false;
        const lineRuleId = String(line?.properties?._cs_free_gift_rule || '');
        if (lineRuleId) return lineRuleId === String(rule.id);
        // Backward compatibility: legacy single-rule gifts had no rule id.
        if (idx === 0 && !lineRuleId && parseInt(line.variant_id, 10) === rule.variantId) return true;
        return false;
      });
      linesForRule.forEach((line) => matchedGiftKeys.add(line.key));

      const subscriptionEligible = window.csSubscriptionIncentive?.qualifies(rule, cart);
      if (subscriptionEligible === true || (subscriptionEligible == null && qualifyingTotal >= rule.thresholdCents)) {
        if (!linesForRule.length) {
          additions.push(rule);
        } else {
          linesForRule.forEach((line, lineIdx) => {
            const nextQty = lineIdx === 0 ? 1 : 0;
            if ((parseInt(line.quantity, 10) || 0) !== nextQty) updates[line.key] = nextQty;
          });
        }
      } else {
        linesForRule.forEach((line) => {
          if ((parseInt(line.quantity, 10) || 0) !== 0) updates[line.key] = 0;
        });
      }
    });

    // Remove orphan gift lines from removed/changed rules.
    giftLines.forEach((line) => {
      if (!matchedGiftKeys.has(line.key)) {
        if ((parseInt(line.quantity, 10) || 0) !== 0) updates[line.key] = 0;
      }
    });

    if (Object.keys(updates).length) {
      await this.updateManyLineItems(updates);
      didMutate = true;
    }

    if (additions.length) {
      for (const rule of additions) {
        if (rule.kind === 'subscription') await window.csSubscriptionIncentive.ensureGiftAvailable();
        await this.addVariant(rule.variantId, '', {
          withSections: false,
          quantity: 1,
          properties: {
            _cs_free_gift: 'true',
            _cs_free_gift_rule: String(rule.id),
          },
        });
        didMutate = true;
      }
    }

    await window.csSubscriptionIncentive?.ensureGiftIsFree((updates) => this.updateManyLineItems(updates));

    if (didMutate) {
      this.requestRefresh({ open: this.classList.contains('active'), immediate: true });
    }
  }

  cacheEmptyStateMarkupFromHtml(html) {
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    this.localizeDrawerDocument(doc);
    const emptyNode = doc.querySelector('#CartDrawer .drawer__inner-empty');
    if (emptyNode) this._cachedEmptyStateMarkup = emptyNode.outerHTML;
  }

  async primeEmptyStateMarkup() {
    if (this._cachedEmptyStateMarkup) return;
    if (this._primeEmptyMarkupPromise) return this._primeEmptyMarkupPromise;
    this._primeEmptyMarkupPromise = fetch(this.sectionsUrl('cart-drawer'), {
      credentials: 'same-origin',
      cache: 'no-store',
    })
      .then((res) => res.json())
      .then((sections) => {
        this.cacheEmptyStateMarkupFromHtml(sections?.['cart-drawer']);
      })
      .catch(() => {})
      .finally(() => {
        this._primeEmptyMarkupPromise = null;
      });
    return this._primeEmptyMarkupPromise;
  }

  applyOptimisticEmptyStateIfNeeded() {
    const items = this.querySelectorAll('.cs-cart-item');
    if (items.length > 0) return false;

    const cartItems = this.querySelector('cart-drawer-items');
    const contents = this.querySelector('#CartDrawer-CartItems');
    if (!cartItems || !contents) return false;
    if (contents.querySelector('.drawer__inner-empty')) {
      this.classList.add('is-empty');
      cartItems.classList.add('is-empty');
      this.setDrawerEmptyVisualState(true);
      this.syncLocalizedChrome();
      return true;
    }

    const template = this.querySelector('#CartDrawer-EmptyStateTemplate');
    contents.innerHTML = '';
    if (template && template.content && template.content.firstElementChild) {
      contents.appendChild(template.content.cloneNode(true));
    } else {
      // No usable inline template: fetch the server markup once so the next empty state is exact.
      if (!this._cachedEmptyStateMarkup) this.primeEmptyStateMarkup();
      const html = this._cachedEmptyStateMarkup
        ? this._cachedEmptyStateMarkup
        : `
          <div class="drawer__inner-empty cs-cart-drawer__empty">
            <button type="button" class="button cs-cart-drawer__continue">${
              this.drawerLabel('continue', 'continueShopping', 'Continue shopping')
            }</button>
            <cs-cart-upsell class="cs-cart-upsell cs-cart-upsell--empty">
              <div class="cs-cart-upsell__box">
                <div class="cs-cart-upsell__top">
                  <div class="cs-cart-upsell__heading">${
                    this.drawerLabel('alsoLike', 'youMayAlsoLike', 'You may also like...')
                  }</div>
                </div>
                <div class="cs-cart-upsell__slider" role="region" aria-label="${
                  window.pdpString ? window.pdpString('youMayAlsoLikeLabel', 'You may also like') : 'You may also like'
                }">
                  <div class="cs-cart-upsell__track cs-cart-upsell__grid" role="list"></div>
                </div>
              </div>
            </cs-cart-upsell>
          </div>
        `;
      contents.innerHTML = html;
    }
    const continueBtn = contents.querySelector('.cs-cart-drawer__continue');
    if (continueBtn) continueBtn.addEventListener('click', () => this.close());

    this.classList.add('is-empty');
    cartItems.classList.add('is-empty');
    this.syncLocalizedChrome();
    this.setDrawerEmptyVisualState(true);
    return true;
  }

  setBusyUI(isBusy, activeItem = null) {
    this.classList.toggle('cs-cart-drawer--busy', isBusy);
    this.toggleAttribute('aria-busy', isBusy);

    // Clear previous item loading state
    this.querySelectorAll('[data-cs-loading="true"]').forEach((el) => el.removeAttribute('data-cs-loading'));

    // Mark active cart entry as loading (CSS shows overlay + spinner)
    if (isBusy && activeItem) activeItem.setAttribute('data-cs-loading', 'true');
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
    cartLink.addEventListener('pointerenter', () => this.ensureUpsellScript(), { passive: true });
    cartLink.addEventListener('focus', () => this.ensureUpsellScript(), { passive: true });
  }

  // The upsell carousel class (cs-cart-upsell) lives in assets/cs-cart-upsell.js and is loaded on the
  // first interaction or drawer open; data-upsell-src is rendered by snippets/cart-drawer.liquid.
  ensureUpsellScript() {
    if (customElements.get('cs-cart-upsell')) return Promise.resolve();
    // Shared across drawer instances (theme-editor re-renders) so the classic script is injected once.
    if (window.__csCartUpsellScriptPromise) return window.__csCartUpsellScriptPromise;
    const src = this.dataset.upsellSrc;
    if (!src) return Promise.resolve();
    window.__csCartUpsellScriptPromise = new Promise((resolve) => {
      const inject = () => {
        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => {
          window.__csCartUpsellScriptPromise = null;
          this.querySelectorAll('cs-cart-upsell').forEach((el) => { el.style.display = 'none'; });
          resolve();
        };
        document.head.appendChild(script);
      };
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', inject, { once: true });
      else inject();
    });
    return window.__csCartUpsellScriptPromise;
  }

  open(triggeredBy) {
    // Upsell carousels are inert while the drawer is closed; load them now (idempotent per element).
    const loadUpsells = () => this.querySelectorAll('cs-cart-upsell').forEach((upsell) => {
      if (typeof upsell.ensureLoaded === 'function') upsell.ensureLoaded();
    });
    if (customElements.get('cs-cart-upsell')) loadUpsells();
    else this.ensureUpsellScript().then(loadUpsells);

    if (triggeredBy) this.setActiveElement(triggeredBy);

    // Ensure we don't show a stale cart (e.g. after editing cart page or returning from checkout).
    const hasPendingWork = this._pendingMutations.size || this.hasPendingGroupedWork();
    if (!hasPendingWork && Date.now() - this._lastRefreshAt > 10000) this.requestRefresh();

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    this.syncLocalizedChrome();
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      this.syncLocalizedChrome();
      this.updateFooterHeightVar();
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);

        // Focus trapping can jump scroll; re-apply if pending.
        if (this._pendingScrollPositions) {
          this.restoreScrollPositions(this._pendingScrollPositions);
          this._pendingScrollPositions = null;
        }
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');

    // Ensure drawer edits are committed as soon as user leaves the drawer,
    // so subsequent add-to-cart actions from elsewhere don't race pending drafts.
    if (this.hasPendingGroupedWork() || this._pendingMutations.size) {
      this.flushAllPendingNow().catch(() => {});
    }
  }

  requestRefresh({ open = false, immediate = false } = {}) {
    this._scheduledRefreshOpen = this._scheduledRefreshOpen || open;

    const run = () => {
      const shouldOpen = this._scheduledRefreshOpen;
      this._scheduledRefreshOpen = false;
      return this.refresh({ open: shouldOpen });
    };

    if (immediate) {
      if (this._scheduledRefreshTimer) {
        clearTimeout(this._scheduledRefreshTimer);
        this._scheduledRefreshTimer = null;
      }
      return run();
    }

    if (this._scheduledRefreshTimer) return this._refreshPromise || Promise.resolve();

    // Coalesce bursts of cart updates into a single section refresh.
    this._scheduledRefreshTimer = setTimeout(() => {
      this._scheduledRefreshTimer = null;
      run();
    }, 45);

    return this._refreshPromise || Promise.resolve();
  }

  trackMutation(promiseLike) {
    const tracked = Promise.resolve(promiseLike).finally(() => {
      this._pendingMutations.delete(tracked);
    });
    this._pendingMutations.add(tracked);
    return tracked;
  }

  queueMutation(task) {
    const run = this._mutationQueue
      .catch(() => {})
      .then(() => task());
    this._mutationQueue = run.catch(() => {});
    return this.trackMutation(run);
  }

  async waitForPendingMutations() {
    while (this._pendingMutations.size) {
      const pending = Array.from(this._pendingMutations);
      await Promise.allSettled(pending);
    }
  }

  hasPendingGroupedWork() {
    return (
      this._groupedMutationDraft.size ||
      Boolean(this._groupedFlushTimer) ||
      Boolean(this._groupedFlushPromise) ||
      Boolean(this._groupedActiveFlushPromise)
    );
  }

  hasPendingCartMutations() {
    return this.hasPendingGroupedWork() || this._pendingMutations.size > 0;
  }

  collectGroupedDraftFromDOM() {
    const drafts = new Map();
    this.querySelectorAll('.cs-cart-item[data-cs-variant-id]').forEach((item) => {
      if (item.dataset.csBundleId) return; // bundle lines change only as a whole bundle
      const signature = this.getGroupedSignatureFromItem(item);
      if (!signature) return;
      const box = item.matches('[data-cs-group-keys]') ? item : item.querySelector('[data-cs-group-keys]');
      if (!box) return;
      const { keys, qtys } = this.parseGroupDataset(box);
      if (!keys.length || keys.length !== qtys.length) return;
      const desiredTotal = qtys.reduce((acc, n) => acc + (parseInt(n, 10) || 0), 0);
      drafts.set(signature, Math.max(0, desiredTotal));
    });
    return drafts;
  }

  readPendingSyncFromStorage() {
    const parsePayload = (raw) => {
      if (!raw) return null;
      try {
        const data = JSON.parse(raw);
        if (!data || !Array.isArray(data.drafts)) return null;
        if (Date.now() - (parseInt(data.ts, 10) || 0) > 15 * 60 * 1000) return null;
        return data;
      } catch (_) {
        return null;
      }
    };

    try {
      const local = parsePayload(localStorage.getItem(this._pendingSyncStorageKey));
      if (local) return local;
    } catch (_) {}

    try {
      return parsePayload(sessionStorage.getItem(this._pendingSyncStorageKey));
    } catch (_) {
      return null;
    }
  }

  clearPendingSyncStorage() {
    try {
      localStorage.removeItem(this._pendingSyncStorageKey);
    } catch (_) {}
    try {
      sessionStorage.removeItem(this._pendingSyncStorageKey);
    } catch (_) {}
  }

  persistPendingSyncToStorage() {
    if (!this.hasPendingCartMutations()) {
      this.clearPendingSyncStorage();
      return;
    }

    const merged = this.collectGroupedDraftFromDOM();
    this._groupedMutationDraft.forEach((qty, signature) => {
      merged.set(signature, Math.max(0, parseInt(qty, 10) || 0));
    });
    if (!merged.size) return;

    const payload = JSON.stringify({
      v: 1,
      ts: Date.now(),
      drafts: Array.from(merged.entries()),
    });

    try {
      localStorage.setItem(this._pendingSyncStorageKey, payload);
      return;
    } catch (_) {}

    try {
      sessionStorage.setItem(this._pendingSyncStorageKey, payload);
    } catch (_) {}
  }

  resumePendingSyncFromStorage() {
    const data = this.readPendingSyncFromStorage();
    if (!data) return;
    this.clearPendingSyncStorage();

    let restored = 0;
    data.drafts.forEach((entry) => {
      if (!Array.isArray(entry) || entry.length < 2) return;
      const signature = String(entry[0] || '');
      if (!signature) return;
      const qty = Math.max(0, parseInt(entry[1], 10) || 0);
      this._groupedMutationDraft.set(signature, qty);
      restored += 1;
    });

    if (!restored) return;
    this._groupedMutationVersion += 1;
    this.scheduleGroupedFlush();
    this.requestRefreshAfterGroupedSettles();
  }

  requestRefreshAfterGroupedSettles() {
    if (this._deferredRefreshTimer) return;
    this._deferredRefreshTimer = setTimeout(() => {
      this._deferredRefreshTimer = null;
      if (this.hasPendingGroupedWork() || this._pendingMutations.size) {
        this.requestRefreshAfterGroupedSettles();
        return;
      }
      this.clearPendingSyncStorage();
      this.requestRefresh({ open: this.classList.contains('active'), immediate: true });
    }, 20);
  }

  queueGroupedUpdatesFromBox(box, { immediate = false } = {}) {
    if (!box) return;
    const { keys, qtys } = this.parseGroupDataset(box);
    if (!keys.length || keys.length !== qtys.length) return;
    const item = box.closest('.cs-cart-item');
    const signature = this.getGroupedSignatureFromItem(item);
    if (!signature) return;
    const desiredTotal = qtys.reduce((acc, n) => acc + (parseInt(n, 10) || 0), 0);
    this._groupedMutationDraft.set(signature, Math.max(0, desiredTotal));
    this.persistPendingSyncToStorage();
    this._groupedMutationVersion += 1;
    if (this._deferredRefreshTimer) {
      clearTimeout(this._deferredRefreshTimer);
      this._deferredRefreshTimer = null;
    }
    if (immediate) {
      this.flushGroupedMutations().catch(() => {
        this.scheduleGroupedFlush();
      });
      return;
    }
    this.scheduleGroupedFlush();
  }

  scheduleGroupedFlush() {
    if (this._groupedFlushTimer || this._groupedFlushPromise || this._groupedActiveFlushPromise) {
      return this._groupedActiveFlushPromise || this._groupedFlushPromise || Promise.resolve();
    }

    this._groupedFlushPromise = new Promise((resolve) => {
      this._groupedFlushTimer = setTimeout(async () => {
        this._groupedFlushTimer = null;
        try {
          await this.flushGroupedMutations();
        } catch (_) {
          // keep unsent updates queued and retry shortly
          this.scheduleGroupedFlush();
        } finally {
          const hasQueuedDraft = this._groupedMutationDraft.size > 0;
          this._groupedFlushPromise = null;
          resolve();
          // If a new draft arrived while the previous flush was running,
          // immediately queue another flush so prices/totals stay in sync.
          if (hasQueuedDraft) this.scheduleGroupedFlush();
        }
      }, 40);
    });

    return this._groupedFlushPromise;
  }

  async flushGroupedMutations() {
    if (this._groupedActiveFlushPromise) return this._groupedActiveFlushPromise;

    const active = this.flushGroupedMutationsWithOptions()
      .catch((err) => {
        throw err;
      })
      .finally(() => {
        if (this._groupedActiveFlushPromise === active) {
          this._groupedActiveFlushPromise = null;
        }
        // Drafts can arrive while an immediate flush is in flight; ensure
        // they are not stranded and get flushed right after this run.
        if (this._groupedMutationDraft.size && !this._groupedFlushTimer && !this._groupedFlushPromise) {
          this.scheduleGroupedFlush();
        }
      });

    this._groupedActiveFlushPromise = active;
    return active;
  }

  async flushGroupedMutationsWithOptions({ keepalive = false } = {}) {
    if (!this._groupedMutationDraft.size) return;
    const flushVersion = this._groupedMutationVersion;
    const desiredBySignature = new Map(this._groupedMutationDraft);
    this._groupedMutationDraft.clear();

    try {
      const cart = await fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' }).then((r) => r.json());
      const items = Array.isArray(cart?.items) ? cart.items : [];

      const updates = {};
      const additions = [];

      desiredBySignature.forEach((desiredQty, signature) => {
        const sig = this.parseGroupedSignature(signature);
        if (!sig) return;
        const lines = this.getCartLinesForGroupedSignature(items, sig);
        const currentTotal = lines.reduce((a, it) => a + (it.quantity || 0), 0);
        const desired = Math.max(0, parseInt(desiredQty, 10) || 0);

        if (desired <= currentTotal) {
          let remaining = desired;
          lines.forEach((it) => {
            const next = Math.max(0, Math.min(it.quantity || 0, remaining));
            updates[it.key] = next;
            remaining -= next;
          });
          return;
        }

        // desired > current
        const delta = desired - currentTotal;
        if (lines.length) {
          const first = lines[0];
          updates[first.key] = (first.quantity || 0) + delta;
          return;
        }

        // Group missing entirely in current cart -> add fresh lines for this signature.
        additions.push({ sig, delta });
      });

      if (Object.keys(updates).length) {
        if (keepalive) {
          const req = fetch('/cart/update.js', {
            method: 'POST',
            credentials: 'same-origin',
            cache: 'no-store',
            keepalive: true,
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ updates }),
          });
          await this.trackMutation(req);
        } else {
          await this.updateManyLineItems(updates);
        }
      }

      if (!keepalive && additions.length) {
        for (const add of additions) {
          if (!add.sig.variantId || add.delta <= 0) continue;
          if (add.sig.purchaseType === 'subscription' && !add.sig.sellingPlanId) continue;
          await this.addVariant(add.sig.variantId, add.sig.sellingPlanId, { withSections: false, quantity: add.delta });
        }
      }

      if (!keepalive && flushVersion === this._groupedMutationVersion) {
        this.requestRefreshAfterGroupedSettles();
      }
    } catch (err) {
      // Re-queue desired grouped totals so checkout/navigation can retry flush.
      desiredBySignature.forEach((qty, signature) => {
        this._groupedMutationDraft.set(signature, qty);
      });
      this.persistPendingSyncToStorage();
      this.endPriceLoading({ clearAll: true });
      throw err;
    }
  }

  getGroupedSignatureFromItem(itemEl) {
    if (!itemEl) return '';
    const variantId = parseInt(itemEl.dataset.csVariantId || '', 10);
    const sellingPlanId = String(itemEl.dataset.csSellingPlanId || '');
    const purchaseType = String(itemEl.dataset.csPurchaseType || 'one-time').toLowerCase();
    if (!variantId) return '';
    return `${variantId}::${sellingPlanId}::${purchaseType}`;
  }

  parseGroupedSignature(signature) {
    if (!signature) return null;
    const parts = String(signature).split('::');
    if (parts.length < 3) return null;
    const variantId = parseInt(parts[0], 10);
    if (!variantId) return null;
    return {
      variantId,
      sellingPlanId: parts[1] || '',
      purchaseType: (parts[2] || 'one-time').toLowerCase(),
    };
  }

  isSubscriptionFromProperties(props) {
    if (!props) return false;
    const keys = Object.keys(props);
    for (const k of keys) {
      const kk = String(k).toLowerCase();
      if (
        kk.includes('shipping_interval_') ||
        kk.includes('order_interval_') ||
        kk.includes('charge_interval_') ||
        kk.includes('recharge') ||
        kk.includes('subscription') ||
        kk.includes('selling_plan')
      ) {
        return true;
      }
    }
    return false;
  }

  getCartLinesForGroupedSignature(items, sig) {
    return items.filter((it) => {
      if (it.variant_id !== sig.variantId) return false;
      if (it.properties && it.properties._cs_bundle_id) return false; // bundle builder lines
      const itSpid = it.selling_plan_allocation?.selling_plan?.id ? String(it.selling_plan_allocation.selling_plan.id) : '';
      if (String(sig.sellingPlanId)) return itSpid === String(sig.sellingPlanId);
      if (sig.purchaseType === 'subscription') return this.isSubscriptionFromProperties(it.properties) || Boolean(it.selling_plan_allocation);
      if (sig.purchaseType === 'one-time') return !this.isSubscriptionFromProperties(it.properties) && !it.selling_plan_allocation;
      return itSpid === '';
    });
  }

  async flushAllPendingNow({ keepalive = false } = {}) {
    if (!this.hasPendingCartMutations()) return;
    if (this._groupedFlushTimer) {
      clearTimeout(this._groupedFlushTimer);
      this._groupedFlushTimer = null;
    }
    if (this._groupedMutationDraft.size) {
      await this.flushGroupedMutationsWithOptions({ keepalive });
    }
    await this.waitForPendingMutations();
    this.clearPendingSyncStorage();
  }

  async onDocumentNavigationIntent(event) {
    if (!this.hasPendingGroupedWork() && !this._pendingMutations.size) return;
    if (this._awaitingCheckoutSubmit) return;

    if (event.type === 'click') {
      const link = event.target?.closest?.('a[href]');
      if (!link) return;
      // Header cart icon is an anchor to /cart; keep drawer-open behavior only.
      if (link.id === 'cart-icon-bubble' || link.closest('#cart-icon-bubble')) return;
      if (link.target === '_blank' || link.hasAttribute('download')) return;
      const href = link.getAttribute('href');
      if (!href || href.startsWith('#') || href.startsWith('javascript:')) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      this.persistPendingSyncToStorage();
      this.flushAllPendingNow({ keepalive: true }).catch(() => {});
      return;
    }

    if (event.type === 'submit') {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (form.id === 'CartDrawer-Form') return; // handled by dedicated checkout guard
      this.persistPendingSyncToStorage();
      this.flushAllPendingNow({ keepalive: true }).catch(() => {});
    }
  }

  onPageHideFlush(evt) {
    if (evt?.type === 'visibilitychange' && document.visibilityState !== 'hidden') return;
    if (!this.hasPendingGroupedWork() && !this._pendingMutations.size) return;
    this.persistPendingSyncToStorage();
    // Best-effort fire before unload/navigation teardown.
    this.flushAllPendingNow({ keepalive: true }).catch(() => {});
  }

  async onCheckoutSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.id !== 'CartDrawer-Form') return;

    const submitter = event.submitter || document.activeElement;
    const isCheckoutSubmitter =
      submitter &&
      (submitter.name === 'checkout' || submitter.id === 'CartDrawer-Checkout' || submitter.getAttribute('form') === 'CartDrawer-Form');
    if (!isCheckoutSubmitter) return;
    event.preventDefault();
    this.startCheckoutFlow(submitter);
  }

  async ensureCheckoutCartIsFinalized() {
    // Run a short stabilization loop so checkout always sees final
    // grouped quantities and free-gift lines.
    for (let i = 0; i < 3; i += 1) {
      if (this._groupedFlushTimer) {
        clearTimeout(this._groupedFlushTimer);
        this._groupedFlushTimer = null;
      }

      if (this._groupedMutationDraft.size) await this.flushGroupedMutations();
      await this.waitForPendingMutations();
      await this.flushAllPendingNow().catch(() => {});

      await this.scheduleFreeGiftSync({ immediate: true });
      if (this._freeGiftSyncError && window.csSubscriptionIncentive?.isAssigned()) throw this._freeGiftSyncError;
      await this.waitForPendingMutations();
      await this.flushAllPendingNow().catch(() => {});

      const hasPendingGrouped = this.hasPendingGroupedWork();
      const hasPendingMutations = this.hasPendingCartMutations();
      const hasFreeGiftSyncWork = Boolean(this._freeGiftSyncPromise || this._freeGiftSyncTimer || this._freeGiftSyncQueued);
      if (!hasPendingGrouped && !hasPendingMutations && !hasFreeGiftSyncWork) break;
    }
  }

  async startCheckoutFlow(submitter) {
    const hasPendingWork = this._pendingMutations.size || this.hasPendingGroupedWork();
    if (this._awaitingCheckoutSubmit) return;

    this._awaitingCheckoutSubmit = true;
    this._checkoutBusyState = true;
    this.applyCheckoutBusyState();

    try {
      if (hasPendingWork) {
        if (this._groupedFlushTimer) {
          clearTimeout(this._groupedFlushTimer);
          this._groupedFlushTimer = null;
        }
        if (this._groupedMutationDraft.size) await this.flushGroupedMutations();
        await this.waitForPendingMutations();
        if (this._groupedMutationDraft.size) {
          await this.flushGroupedMutations();
          await this.waitForPendingMutations();
        }
      }
      await this.ensureCheckoutCartIsFinalized();
      window.location.assign('/checkout');
    } catch (error) {
      const message = this.querySelector('#CartDrawer-CartErrors');
      if (message) message.textContent = window.csSubscriptionIncentive?.errorMessage() || error.message;
    } finally {
      this._awaitingCheckoutSubmit = false;
      this._checkoutBusyState = false;
      this.applyCheckoutBusyState();
    }
  }

  drawerLabel(name, pdpKey, fallback) {
    const fromAttr = {
      empty: this.dataset.labelEmpty,
      title: this.dataset.labelTitle,
      add: this.dataset.labelAdd,
      preorder: this.dataset.labelPreorder,
      continue: this.dataset.labelContinue,
      alsoLike: this.dataset.labelAlsoLike,
    }[name];
    if (typeof window.pdpString === 'function') {
      return window.pdpString(pdpKey, fromAttr || fallback || '');
    }
    return fromAttr || fallback || '';
  }

  localizeDrawerDocument(doc) {
    if (!doc || !doc.querySelector) return;
    const empty = this.drawerLabel('empty', 'emptyCart', 'Your cart is empty');
    const title = this.drawerLabel('title', 'cartTitle', 'Your cart');
    const add = this.drawerLabel('add', 'add', 'Add');
    const preorder = this.drawerLabel('preorder', 'preorder', 'Preorder');
    const continueLabel = this.drawerLabel('continue', 'continueShopping', 'Continue shopping');
    const alsoLike = this.drawerLabel('alsoLike', 'youMayAlsoLike', 'You may also like...');
    const isEmpty = Boolean(doc.querySelector('.cs-cart-drawer__empty, .drawer__inner-empty'));

    doc.querySelectorAll('.cs-cart-drawer__heading').forEach((heading) => {
      heading.textContent = isEmpty ? empty : title;
    });
    doc.querySelectorAll('.cs-cart-upsell__add-text').forEach((el) => {
      const btn = el.closest('[data-cs-preorder], .cs-cart-upsell__add');
      const isPreorder = String(btn?.getAttribute('data-cs-preorder') || '').toLowerCase() === 'true';
      el.textContent = isPreorder ? preorder : add;
    });
    doc.querySelectorAll('.cs-cart-drawer__continue').forEach((el) => {
      el.textContent = continueLabel;
    });
    doc.querySelectorAll('.cs-cart-upsell__heading').forEach((el) => {
      el.textContent = alsoLike;
    });
    doc.querySelectorAll('[data-cs-heading]').forEach((el) => {
      el.setAttribute('data-cs-heading', alsoLike);
    });
    doc.querySelectorAll('cs-cart-upsell').forEach((el) => {
      el.setAttribute('data-label-add', add);
      el.setAttribute('data-label-preorder', preorder);
      el.setAttribute('data-cs-heading', alsoLike);
    });
  }

  syncLocalizedChrome() {
    this.localizeDrawerDocument(this);

    const template = this.querySelector('#CartDrawer-EmptyStateTemplate');
    if (template && template.content) this.localizeDrawerDocument(template.content);

    const dialog = this.querySelector('.drawer__inner[aria-label]');
    if (dialog) {
      dialog.setAttribute('aria-label', this.drawerLabel('title', 'cartTitle', 'Your cart'));
    }
  }

  sectionsUrl(sections) {
    if (typeof window.shopifySectionsUrl === 'function') {
      return window.shopifySectionsUrl(sections, this.dataset.rootUrl);
    }
    const root = this.dataset.rootUrl || '/';
    const normalized = root.charAt(root.length - 1) === '/' ? root : root + '/';
    return normalized + '?sections=' + sections;
  }

  refresh({ open = false } = {}) {
    // Serialize refreshes so a refresh requested during an in-flight refresh is NOT dropped.
    const prev = this._refreshPromise || Promise.resolve();
    this._refreshPromise = prev.finally(() => this._doRefresh({ open }));
    return this._refreshPromise;
  }

  _doRefresh({ open = false } = {}) {
    const previousScrollPositions = this.captureScrollPositions();
    const wasActive = this.classList.contains('active');
    this._pendingScrollPositions = previousScrollPositions;
    // Refresh drawer + cart bubble without forcing open.
    return fetch(this.sectionsUrl('cart-drawer,cart-icon-bubble'), { credentials: 'same-origin' })
      .then((res) => res.json())
      .then((sections) => {
        const parsedState = { id: this.productId, sections };
        this.cacheEmptyStateMarkupFromHtml(parsedState.sections['cart-drawer']);
        this.getSectionsToRender().forEach((section) => {
          if (section.id === 'cart-drawer') {
            this.patchCartDrawerSection(parsedState.sections[section.id]);
            return;
          }
          const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
          if (!sectionElement) return;
          sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
        });

        // Rebind overlay click (DOM replaced)
        const overlay = this.querySelector('#CartDrawer-Overlay');
        overlay && overlay.addEventListener('click', this.close.bind(this));

        // Prevent open() from triggering another refresh due to staleness heuristics.
        this._lastRefreshAt = Date.now();
        this.syncEmptyStateClass();
        this.syncLocalizedChrome();
        this.syncGroovyCreditProgress();
        this.updateFooterHeightVar();
        this.restoreScrollPositions(previousScrollPositions);
        // Don't re-open if already open; it can reset scroll via focus-trap.
        if (open && !wasActive) this.open();
      })
      .catch(() => {})
      .finally(() => {
        this._lastRefreshAt = Date.now();
        this.syncEmptyStateClass();
        this.applyCheckoutBusyState();
        this.syncGroovyCreditProgress();
        this.updateFooterHeightVar();
        this.restoreScrollPositions(previousScrollPositions);
        this.hydrateGiftCheckpointImages();
        this.scheduleFreeGiftSync();
        this.endPriceLoading({ deferMs: 60, clearAll: true });
      });
  }

  parseGroupDataset(el) {
    const keys = (el.dataset.csGroupKeys || '').split('||').filter(Boolean);
    const qtys = (el.dataset.csGroupQtys || '').split('||').filter(Boolean).map((n) => parseInt(n, 10) || 0);
    return { keys, qtys };
  }

  serializeGroupQtys(qtys) {
    return qtys.map((n) => String(Math.max(0, parseInt(n, 10) || 0))).join('||');
  }

  applyOptimisticGroupedQty(box, activeItem, action) {
    const { keys, qtys } = this.parseGroupDataset(box);
    if (!keys.length || keys.length !== qtys.length) return null;

    const nextQtys = [...qtys];
    if (action === 'plus') {
      nextQtys[0] = (nextQtys[0] || 0) + 1;
    } else if (action === 'minus') {
      for (let i = nextQtys.length - 1; i >= 0; i--) {
        if ((nextQtys[i] || 0) > 0) {
          nextQtys[i] = nextQtys[i] - 1;
          break;
        }
      }
    }

    const total = nextQtys.reduce((a, n) => a + (n || 0), 0);
    box.dataset.csGroupQtys = this.serializeGroupQtys(nextQtys);

    if (activeItem) {
      const qtyCount = activeItem.querySelector('.cs-qty__count');
      if (qtyCount) qtyCount.textContent = String(total);
      const minusBtn = activeItem.querySelector('[data-cs-qty-action="minus"]');
      if (minusBtn) minusBtn.classList.toggle('disabled', total <= 1);
    }

    return { keys, qtys };
  }

  applyOptimisticGroupedRemove(box, activeItem) {
    const { keys, qtys } = this.parseGroupDataset(box);
    if (!keys.length || keys.length !== qtys.length) return null;

    const parent = activeItem?.parentElement || null;
    const nextSibling = activeItem?.nextSibling || null;
    if (activeItem) activeItem.remove();
    box.dataset.csGroupQtys = this.serializeGroupQtys(qtys.map(() => 0));

    return { keys, qtys, activeItem, parent, nextSibling };
  }

  getGroupLinesFromDOM(el) {
    if (!el) return [];
    const { keys, qtys } = this.parseGroupDataset(el);
    if (!keys.length || keys.length !== qtys.length) return [];
    return keys.map((key, i) => ({ key, quantity: qtys[i] || 0 })).filter((it) => it.key);
  }

  /**
   * Cart Ajax paths under the shopper's market and language root (/es-mx/cart/change.js).
   * Without it Shopify renders the returned sections for the primary market, so a Mexico
   * drawer came back with US content, such as the US empty-cart upsells.
   */
  marketUrl(url) {
    if (typeof url !== 'string' || !/^\/cart(\/|\.js)/.test(url)) return url;
    const root = typeof window.shopifyRoot === 'function' ? window.shopifyRoot(this.dataset.rootUrl) : '/';
    return root === '/' ? url : root + url.slice(1);
  }

  async requestJson(url, options = {}) {
    const res = await fetch(this.marketUrl(url), options);
    let data = null;
    try {
      data = await res.json();
    } catch (e) {
      // ignore
    }
    if (!res.ok) {
      const err = new Error(`Request failed: ${res.status}`);
      err.status = res.status;
      err.data = data;
      throw err;
    }
    return data;
  }

  getSectionRenderPayload() {
    return {
      sections: this.getSectionsToRender().map((section) => section.id),
      sections_url: window.location.pathname,
    };
  }

  applySectionsFromState(state) {
    if (!state || !state.sections) return false;
    this.renderContents(state);
    return true;
  }

  patchCartDrawerSection(html) {
    if (!html) return false;
    this.cacheEmptyStateMarkupFromHtml(html);
    const doc = new DOMParser().parseFromString(html, 'text/html');
    this.localizeDrawerDocument(doc);
    const selectors = [
      '#CartDrawer .cs-cart-drawer__header',
      '#CartDrawer .groovy-credit-progress',
      '#CartDrawer cart-drawer-items',
      '#CartDrawer .drawer__footer',
    ];

    let replaced = false;
    selectors.forEach((selector) => {
      const target = this.querySelector(selector);
      const source = doc.querySelector(selector);
      if (!target || !source) return;
      target.replaceWith(source.cloneNode(true));
      replaced = true;
    });

    // Upsert free-gift progress block; it lives outside cart-drawer-items and
    // must be patched explicitly so dynamic qty/add/remove updates stay in sync.
    const readGiftProgressPercent = (node) => {
      if (!node) return null;
      const inlineValue = (node.style && node.style.getPropertyValue('--cs-free-gift-progress')) || '';
      const computedValue = window.getComputedStyle ? window.getComputedStyle(node).getPropertyValue('--cs-free-gift-progress') : '';
      const raw = String(inlineValue || computedValue || '').replace('%', '').trim();
      const n = parseFloat(raw);
      if (!Number.isFinite(n)) return null;
      return Math.max(0, Math.min(100, n));
    };
    const animateGiftProgressChange = (node, fromPercent, toPercent) => {
      if (!node) return;
      if (!Number.isFinite(fromPercent) || !Number.isFinite(toPercent)) return;
      if (Math.abs(fromPercent - toPercent) < 0.1) return;
      node.style.setProperty('--cs-free-gift-progress', `${fromPercent}%`);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          node.style.setProperty('--cs-free-gift-progress', `${toPercent}%`);
        });
      });
    };

    const giftSelector = '#CartDrawer .cs-cart-drawer__gift-progress';
    const giftTarget = this.querySelector(giftSelector);
    const giftSource = doc.querySelector(giftSelector);
    const header = this.querySelector('#CartDrawer .cs-cart-drawer__header');
    const drawerItems = this.querySelector('#CartDrawer cart-drawer-items');
    if (giftSource && giftTarget) {
      const fromPercent = readGiftProgressPercent(giftTarget);
      const nextNode = giftSource.cloneNode(true);
      const toPercent = readGiftProgressPercent(nextNode);
      giftTarget.replaceWith(nextNode);
      animateGiftProgressChange(nextNode, fromPercent, toPercent);
      replaced = true;
    } else if (giftSource && !giftTarget) {
      const node = giftSource.cloneNode(true);
      if (header && header.parentElement) header.insertAdjacentElement('afterend', node);
      else if (drawerItems && drawerItems.parentElement) drawerItems.parentElement.insertBefore(node, drawerItems);
      replaced = true;
    } else if (!giftSource && giftTarget) {
      giftTarget.remove();
      replaced = true;
    }

    // Upsert non-empty upsell block; when cart goes empty->non-empty, target may not exist yet.
    const upsellSelector = '#CartDrawer cs-cart-upsell:not(.cs-cart-upsell--empty)';
    const upsellTarget = this.querySelector(upsellSelector);
    const upsellSource = doc.querySelector(upsellSelector);
    const drawerInner = this.querySelector('#CartDrawer .drawer__inner');
    const footer = this.querySelector('#CartDrawer .drawer__footer');
    if (upsellSource && upsellTarget) {
      upsellTarget.replaceWith(upsellSource.cloneNode(true));
      replaced = true;
    } else if (upsellSource && !upsellTarget && drawerInner) {
      const node = upsellSource.cloneNode(true);
      if (footer && footer.parentElement === drawerInner) drawerInner.insertBefore(node, footer);
      else drawerInner.appendChild(node);
      replaced = true;
    } else if (!upsellSource && upsellTarget) {
      upsellTarget.remove();
      replaced = true;
    }

    this.syncEmptyStateClass();
    this.syncLocalizedChrome();
    this.syncGroovyCreditProgress();
    return replaced;
  }

  async changeLineItemByKey(key, quantity, { withSections = false } = {}) {
    const payload = { id: key, quantity };
    if (withSections) Object.assign(payload, this.getSectionRenderPayload());

    return this.queueMutation(() =>
      this.requestJson('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
    );
  }

  async updateManyLineItems(updates, { withSections = false } = {}) {
    const payload = { updates };
    if (withSections) Object.assign(payload, this.getSectionRenderPayload());

    return this.queueMutation(() =>
      this.requestJson('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
    );
  }

  async addVariant(variantId, sellingPlanId = '', { withSections = false, quantity = 1, properties = null } = {}) {
    if (!variantId) return;
    const payload = { id: variantId, quantity: Math.max(1, parseInt(quantity, 10) || 1) };
    if (sellingPlanId) payload.selling_plan = sellingPlanId;
    if (properties && typeof properties === 'object' && Object.keys(properties).length) payload.properties = properties;
    if (withSections) Object.assign(payload, this.getSectionRenderPayload());

    return this.queueMutation(() =>
      this.requestJson('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
    );
  }

  async getGroupLinesFromCart(activeItem) {
    const variantId = parseInt(activeItem?.dataset?.csVariantId || '', 10);
    const sellingPlanId = activeItem?.dataset?.csSellingPlanId || '';
    const purchaseType = (activeItem?.dataset?.csPurchaseType || '').toLowerCase();
    if (!variantId) return [];

    const cart = await fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' }).then((r) => r.json());
    if (!cart || !Array.isArray(cart.items)) return [];

    const isSubFromProps = (props) => {
      if (!props) return false;
      const keys = Object.keys(props);
      for (const k of keys) {
        const kk = String(k).toLowerCase();
        if (
          kk.includes('shipping_interval_') ||
          kk.includes('order_interval_') ||
          kk.includes('charge_interval_') ||
          kk.includes('recharge') ||
          kk.includes('subscription') ||
          kk.includes('selling_plan')
        ) {
          return true;
        }
      }
      return false;
    };

    return cart.items
      .filter((it) => {
        const itSpid = it.selling_plan_allocation?.selling_plan?.id ? String(it.selling_plan_allocation.selling_plan.id) : '';
        if (it.variant_id !== variantId) return false;
        if (it.properties && it.properties._cs_bundle_id) return false; // bundle builder lines
        if (String(sellingPlanId)) return itSpid === String(sellingPlanId);
        // No selling plan id available → split by purchase type marker
        if (purchaseType === 'subscription') return isSubFromProps(it.properties) || Boolean(it.selling_plan_allocation);
        if (purchaseType === 'one-time') return !isSubFromProps(it.properties) && !it.selling_plan_allocation;
        return itSpid === '';
      })
      .map((it) => ({ key: it.key, quantity: it.quantity }))
      .filter((it) => it.key);
  }

  async onGroupedQtyClick(event, button) {
    const box = button.closest('[data-cs-group-keys]');
    if (!box) return;
    event.preventDefault();

    const activeItem = button.closest('.cs-cart-item');
    if (String(activeItem?.dataset?.csLocked || '').toLowerCase() === 'true') return;

    const action = button.dataset.csQtyAction;
    const variantId = parseInt(activeItem?.dataset?.csVariantId || '', 10);
    const sellingPlanId = activeItem?.dataset?.csSellingPlanId || '';
    const purchaseType = (activeItem?.dataset?.csPurchaseType || '').toLowerCase();
    let group = this.getGroupLinesFromDOM(box);
    if (!group.length) {
      const priceLoadingKey = this.beginPriceLoading(activeItem);
      if (action === 'plus' && variantId && (sellingPlanId || purchaseType !== 'subscription')) {
        try {
          this.adjustCartBubbleCount(1);
          await this.addVariant(variantId, sellingPlanId);
          this.requestRefresh({ open: this.classList.contains('active'), immediate: true });
        } catch (_) {
          // swallow
          this.adjustCartBubbleCount(-1);
          this.endPriceLoading({ key: priceLoadingKey });
        }
      } else {
        // Metadata mismatch or stale DOM: force a hard section refresh so
        // row prices/subtotal recover to server-truth before next mutation.
        this.requestRefresh({ open: this.classList.contains('active'), immediate: true });
      }
      return;
    }

    const totalQty = group.reduce((a, it) => a + (it.quantity || 0), 0);
    if (action === 'minus' && totalQty <= 1) return;

    const priceLoadingKey = this.beginPriceLoading(activeItem);
    const optimisticSnapshot = this.applyOptimisticGroupedQty(box, activeItem, action);
    this.adjustCartBubbleCount(action === 'plus' ? 1 : -1);

    try {
      // Qty taps should feel immediate; flush grouped mutation right away.
      this.queueGroupedUpdatesFromBox(box, { immediate: true });
    } catch (e) {
      // swallow
      this.adjustCartBubbleCount(action === 'plus' ? -1 : 1);
      if (optimisticSnapshot && box) {
        box.dataset.csGroupQtys = this.serializeGroupQtys(optimisticSnapshot.qtys);
      }
      this.endPriceLoading({ key: priceLoadingKey });
    }
  }

  async onGroupedRemoveClick(event, button) {
    const box = button.closest('[data-cs-group-keys]');
    if (!box) return;
    event.preventDefault();

    const activeItem = button.closest('.cs-cart-item');
    if (String(activeItem?.dataset?.csLocked || '').toLowerCase() === 'true') return;
    const priceLoadingKey = this.beginPriceLoading(activeItem);
    const optimisticSnapshot = this.applyOptimisticGroupedRemove(box, activeItem);
    const isNowEmpty = this.applyOptimisticEmptyStateIfNeeded();
    const removedTotal = optimisticSnapshot?.qtys?.reduce((a, n) => a + (parseInt(n, 10) || 0), 0) || 0;
    if (removedTotal) this.adjustCartBubbleCount(-removedTotal);

    try {
      this.queueGroupedUpdatesFromBox(box, { immediate: isNowEmpty });
    } catch (e) {
      // swallow
      if (removedTotal) this.adjustCartBubbleCount(removedTotal);
      if (optimisticSnapshot?.parent && optimisticSnapshot?.activeItem) {
        if (optimisticSnapshot.nextSibling && optimisticSnapshot.nextSibling.parentNode === optimisticSnapshot.parent) {
          optimisticSnapshot.parent.insertBefore(optimisticSnapshot.activeItem, optimisticSnapshot.nextSibling);
        } else {
          optimisticSnapshot.parent.appendChild(optimisticSnapshot.activeItem);
        }
      }
      if (optimisticSnapshot?.qtys && box) {
        box.dataset.csGroupQtys = this.serializeGroupQtys(optimisticSnapshot.qtys);
      }
      this.endPriceLoading({ key: priceLoadingKey });
    }
  }

  // Bundle builder lines (sections/bundle-builder.liquid) share _cs_bundle_id and leave the
  // cart together: the bundle discount needs the whole set. The drawer shows each bundle as
  // one block (snippets/cart-drawer-bundle.liquid) with a single remove button.
  async onBundleRemoveClick(event, button) {
    event.preventDefault();
    const bundleId = String(button.dataset.csRemoveBundle || '');
    if (!bundleId || button.disabled) return;
    button.disabled = true;

    const group = button.closest('[data-cs-bundle-group]');
    const activeItem = group || button.closest('.cs-cart-item');
    if (group) group.setAttribute('data-cs-loading', 'true');
    const priceLoadingKey = this.beginPriceLoading(activeItem);
    const open = this.classList.contains('active');

    try {
      await this.flushAllPendingNow();
      const cart = await this.requestJson('/cart.js', { credentials: 'same-origin', cache: 'no-store' });
      const updates = {};
      (cart?.items || []).forEach((item) => {
        if (String(item?.properties?._cs_bundle_id || '') === bundleId) updates[item.key] = 0;
      });
      if (Object.keys(updates).length) {
        const state = await this.updateManyLineItems(updates, { withSections: true });
        if (!this.applySectionsFromState(state)) await this.requestRefresh({ open, immediate: true });
      } else {
        await this.requestRefresh({ open, immediate: true });
      }
      document.dispatchEvent(
        new CustomEvent('cart:updated', { bubbles: true, detail: { source: 'cart-drawer-bundle', skipDrawerRefresh: true } })
      );
    } catch (_) {
      this.requestRefresh({ open, immediate: true });
    } finally {
      this.endPriceLoading({ key: priceLoadingKey });
      // Still on screen only when the refresh failed: let the shopper try again.
      if (group && group.isConnected) {
        group.removeAttribute('data-cs-loading');
        button.disabled = false;
      }
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const hasPendingWork = this.hasPendingGroupedWork() || this._pendingMutations.size;
    if (hasPendingWork) {
      this.requestRefreshAfterGroupedSettles();
      if (!this.classList.contains('active')) this.open();
      return;
    }

    const previousScrollPositions = this.captureScrollPositions();
    const wasActive = this.classList.contains('active');
    const skipAutoOpenOnce = this.dataset.psmSkipAutoOpenOnce === 'true';
    if (skipAutoOpenOnce) delete this.dataset.psmSkipAutoOpenOnce;
    const suppressUntil = parseInt(this.dataset.psmSuppressRefreshUntil || '0', 10) || 0;
    const suppressBubbleSection = Date.now() < suppressUntil;
    this._pendingScrollPositions = previousScrollPositions;
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      if (section.id === 'cart-icon-bubble' && suppressBubbleSection) return;
      if (section.id === 'cart-drawer') {
        this.patchCartDrawerSection(parsedState.sections[section.id]);
        return;
      }
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.syncEmptyStateClass();
      this.syncLocalizedChrome();
      this.syncGroovyCreditProgress();
      this.updateFooterHeightVar();
      this.restoreScrollPositions(previousScrollPositions);
      this.hydrateGiftCheckpointImages();
      this.scheduleFreeGiftSync();
      if (!wasActive && !skipAutoOpenOnce) this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return '';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const node = doc.querySelector(selector);
    // Some responses return only the inner HTML (no wrapper matching selector)
    return node ? node.innerHTML : doc.body.innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    if (!html) return null;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.querySelector(selector) || doc.body.firstElementChild;
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  onCartUpdate() {
    const drawer = this.closest('cart-drawer') || document.querySelector('cart-drawer');
    if (drawer && typeof drawer.requestRefresh === 'function') {
      drawer.requestRefresh({ immediate: true });
      return;
    }
    super.onCartUpdate();
  }

  updateQuantity(line, quantity, name, variantId) {
    const result = super.updateQuantity(line, quantity, name, variantId);
    Promise.resolve(result).finally(() => {
      const drawer = document.querySelector('cart-drawer');
      if (!drawer) return;
      if (typeof drawer.syncEmptyStateClass === 'function') drawer.syncEmptyStateClass();
      if (typeof drawer.syncLocalizedChrome === 'function') drawer.syncLocalizedChrome();
    });
    return result;
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
